import React from 'react'
import styles from './GarminFlow.scss'
import { S2DLayout } from '../../S2DLayout'
import { s2dMessages } from '../../intlMessages'
import payoffImg from 'assets/images/send_to_device/payoff.svg'
import { Icon } from 'common/Icon'
import { CTA } from 'common/styleguide/CTA'
import { infoMessage } from 'lib/styleguide/base.scss'
import { Msg, commonCTAs } from 'lib/localization'
import _ from 'lodash'
import { FormattedMessage as T } from 'react-intl'

export const GarminFlow = ({ subtitle, selectedDevice, step, routeId, route, onPrev, onNext }) => (
  <S2DLayout
    title={<Msg m={s2dMessages.sendTo} values={{ device: 'Garmin Edge' }} />}
    subtitle={subtitle}
    onClickPrev={onPrev}
    onClickNext={step !== 6 ? onNext : () => { window.location.href = `/routes/${routeId}` }}
    nextText={step !== 6 ? <Msg capitalized m={commonCTAs.next} /> : <Msg m={s2dMessages.returnToRoute} />}
    step={step}
    stepCount={6}>
    <div className={styles.GarminFlow}>
      {step === 2 && <Step2 routeId={routeId} route={route} />}
      {step === 3 && <Step3 />}
      {step === 4 && <Step4 />}
      {step === 5 && <Step5 />}
      {step === 6 && <Step6 />}
    </div>
  </S2DLayout>
)

const turnTooltip = 'We will display a notification 30 meters before each cue in this route. Click to learn how to customize these settings.'

const Step2 = ({ routeId, route }) => {
  const advancedTurns = (
    route &&
    _.get(route, '_permissions.customize_export_options', false) &&
    route.coursePoints &&
    route.coursePoints.length > 0
  )

  const tcxURL = advancedTurns
    ? `/routes/${routeId}.tcx?turn_notification_distance=30`
    : `/routes/${routeId}.tcx`
  return (
    <div className={styles.Step2}>
      <div className={styles.instructions}>
        <p>
          <T id='s2d.garmin21' defaultMessage='Download the appropriate file for your Garmin Edge:' />
        </p>
        <p>
          <T id='s2d.garmin22' defaultMessage="Most Garmin Edge series devices use TCX route files. Click the button to download a TCX file of your route, which will be saved in your computer's Downloads folder:" />
        </p>
        <p>
          <CTA inline href={tcxURL}>
            <Icon i='md-download' size={14} /> Download TCX File
          </CTA>
        </p>
        {advancedTurns && <p className={infoMessage}>
          <T
            id='s2d.garmin23'
            defaultMessage='This file will be exported with 30 meter advanced turn notifications <a>{icon}</a>'
            values={{
              a: (...c) => <a
                data-tip
                title={turnTooltip}
                target='_blank'
                className={styles.helpIcon}
                href='https://ridewithgps.com/help/export-file-formats#turnnotifications'>{c}</a>,
              icon: <Icon i='help-circled' />
            }} />
        </p>}
        <p className={styles.footnotes}>
          <T
            id='s2d.garmin24'
            defaultMessage='For older Garmin devices that require GPX files, please <a>export the route file manually</a>.'
            values={{
              a: (...c) => <a href='https://ridewithgps.com/help/export-routes-to-garmin-device' target='_blank'>{c}</a>
            }} />
        </p>
      </div>
    </div>
  )
}

const Step3 = () => (
  <div className={styles.vidoColumn}>
    <div className={styles.leftCol}>
      <p>
        <T id='s2d.garmin31' defaultMessage='Use a USB data cable to plug your Garmin Edge into your computer.' />
      </p>
      <p>
        <T
          id='s2d.garmin32'
          defaultMessage="Once your device appears in the computer's file browser, drag your downloaded TCX route file from your computer's <strong>Downloads</strong> folder to the <strong>NewFiles</strong> folder on your Garmin."
          values={{
            strong: (...c) => <strong>{c}</strong>
          }} />
      </p>
    </div>
    <div className={styles.rightCol}>
      <video width='449' height='354' autoPlay muted loop key='step3'>
        <source src='/images/routes/transfer_garmin.webm' type='video/webm' />
        <source src='/images/routes/transfer_garmin.mp4' type='video/mp4' />
      </video>
    </div>
  </div>
)

const Step4 = () => (
  <div className={styles.vidoColumn}>
    <div className={styles.leftCol}>
      <p>
        <T id='s2d.garmin41' defaultMessage='Safely disconnect your Garmin Edge from your computer.' />
      </p>
      <p>
        <T
          id='s2d.garmin42'
          defaultMessage='Use your file browser to <strong>Eject</strong> (Mac) or <strong>Safely Remove</strong> (Windows) your Garmin, and unplug it'
          values={{
            strong: (...c) => <strong>{c}</strong>
          }} />
      </p>
      <p>
        <T id='s2d.garmin43' defaultMessage='Your Garmin Edge will reboot to the home screen.' />
      </p>
    </div>
    <div className={styles.rightCol}>
      <video width='449' height='256' autoPlay muted loop key='step4'>
        <source src='/images/routes/eject_garmin.webm' type='video/webm' />
        <source src='/images/routes/eject_garmin.mp4' type='video/mp4' />
      </video>
    </div>
  </div>
)

const Step5 = () => (
  <div className={styles.Step5}>
    <div className={styles.leftCol}>
      <p>
        <T id='s2d.garmin51' defaultMessage='Use the menu on your Garmin Edge to select your downloaded route for navigation.' />
      </p>
      <p>
        <T id='s2d.garmin52' defaultMessage="This process varies depending on the type of device you have. On most Garmin Edge devices, routes will appear under 'Training' or 'Courses' in the main menu." />
      </p>
    </div>
    <div className={styles.rightCol}>
      <video width='200' height='265' autoPlay muted loop key='step5' className={styles.videoBorder}>
        <source src='/images/routes/garmin_select_route.webm' type='video/webm' />
        <source src='/images/routes/garmin_select_route.mp4' type='video/mp4' />
      </video>
    </div>
  </div>
)

const Step6 = () => (
  <div className={styles.Step6}>
    <div className={styles.instructions}>
      <p>
        <T id='s2d.garmin61' defaultMessage='After a brief warm up, your Garmin Edge will be ready to navigate your route.' />
      </p>
      <p><Msg m={s2dMessages.enjoyYrRide} /></p>
      <div className={styles.centeredImg}>
        <img src={payoffImg} width='150' height='150' />
      </div>
    </div>
  </div>
)
